import React, { useEffect, useRef } from "react";
import "./style.scss";
import { demonstrationStore } from "../../store/Demonstration";
import HandImage from "../../assets/images/hand.png";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Game from "../../store/Game";
import useCurrentUserData from "../../hooks/useCurrentUserData";
import { shopStore } from "../../store/Shop";

export default observer(function Demonstration() {
  const { t } = useTranslation();
  const handRef = useRef(null);
  const helpRef = useRef(null);
  const location = useLocation();
  const step = demonstrationStore.step;
  const isOpen = demonstrationStore.isOpen;
  const { getUserData } = useCurrentUserData();

  useEffect(() => {
    const rootContainer = document.getElementsByTagName("body");
    if (rootContainer.length) {
      rootContainer[0].style.removeProperty("overflow");
    }
    const handlePositionUpdate = () => {
      const targetEl = demonstrationStore.refCard.current;
      if (!handRef.current || !helpRef.current) return;
      // нет элемента, на который указываем, — не показываем подсказку вовсе: иначе
      // она оставалась с координатами по умолчанию в левом верхнем углу (28.09)
      const visibility = targetEl ? "visible" : "hidden";
      helpRef.current.style.visibility = visibility;
      handRef.current.style.visibility = visibility;
      if (!targetEl) return;
      // 28.09: цель может оказаться ниже первого экрана (пустые первые зоны →
      // подсказка на 3-й зоне). Скроллить сам пользователь не может — оверлей
      // перехватывает колесо/тач, — и видел только затемнённый экран. Докручиваем
      // цель в центр; мгновенно, чтобы rect ниже был уже после прокрутки.
      const targetRect = targetEl.getBoundingClientRect();
      if (targetRect.top < 0 || targetRect.bottom > window.innerHeight) {
        targetEl.scrollIntoView({ block: "center" });
      }
      // Оверлей и подсказки — position:fixed, поэтому используем viewport-
      // координаты getBoundingClientRect() как есть: прибавление scrollTop
      // задваивало прокрутку и рука/тултип уезжали вниз при скролле.
      const rect = targetEl.getBoundingClientRect();
      const widthWindow = window.innerWidth;
      let helpLeft, helpTop, handX, handY, arrowPos;
      switch (step) {
        case 1:
          if (widthWindow > 475) {
            helpLeft = rect.left + rect.width * 1.45;
            helpTop = rect.top;
            handX = rect.left + rect.width * 0.75;
            handY = rect.top + rect.height / 2;
          } else {
            helpLeft = rect.left + rect.width * 1.2;
            helpTop = rect.top - rect.height * 0.1;
            handX = rect.left + rect.width * 0.75;
            handY = rect.top + rect.height / 2;
          }
          arrowPos = "top-right";
          break;
        case 2:
          if (widthWindow > 475) {
            helpLeft = rect.left + rect.width * 5;
            helpTop = rect.top - rect.height;
            handX = rect.left + rect.width;
            handY = rect.top + rect.height;
          } else {
            helpLeft = rect.left + rect.width * 1.6;
            helpTop = rect.top - rect.height * 1.2;
            handX = rect.left + rect.width;
            handY = rect.top + rect.height;
          }

          arrowPos = "top-left";
          break;
        case 3:
          if (widthWindow > 475) {
            helpLeft = rect.left + rect.width * 4.5;
            helpTop = rect.top + rect.height * 3.5;
            handX = rect.left + rect.width * 0.8;
            handY = rect.top + rect.height;
          } else {
            helpLeft = rect.left - rect.width * 1.8;
            helpTop = rect.top + rect.height * 3.2;
            handX = rect.left + rect.width * 0.8;
            handY = rect.top + rect.height;
          }
          arrowPos = "bottom-center";
          break;
        case 4: {
          const iconCenterX = rect.left + rect.width / 2;
          handX = iconCenterX - 4;
          handY = rect.top + rect.height / 2 + 38;
          const halfBubble = widthWindow > 475 ? 140 : 130;
          helpLeft = Math.max(
            halfBubble + 12,
            Math.min(iconCenterX, widthWindow - halfBubble - 12)
          );
          helpTop = rect.top - (widthWindow > 475 ? 130 : 140);
          arrowPos = "bottom-center";
          break;
        }
        default:
          if (widthWindow > 475) {
            helpLeft = rect.left + rect.width * 4.5;
            helpTop = rect.top + rect.height * 3.5;
            handX = rect.left + rect.width * 0.8;
            handY = rect.top + rect.height;
          } else {
            helpLeft = rect.left - rect.width * 1.8;
            helpTop = rect.top + rect.height * 3.2;
            handX = rect.left + rect.width * 0.8;
            handY = rect.top + rect.height;
          }
          arrowPos = "bottom-center";
          break;
      }

      helpRef.current.style.setProperty("--help-left-px", `${helpLeft}px`);
      helpRef.current.style.setProperty("--help-top-px", `${helpTop}px`);

      handRef.current.style.setProperty("--hand-left-px", `${handX}px`);
      handRef.current.style.setProperty("--hand-top-px", `${handY}px`);

      helpRef.current.dataset.arrow = arrowPos;
    };
    handlePositionUpdate();

    // capture на document: страница скроллится внутренним контейнером, а его
    // scroll не всплывает до window — подсказка не ехала вслед за карточкой
    document.addEventListener("scroll", handlePositionUpdate, true);
    window.addEventListener("resize", handlePositionUpdate);

    return () => {
      document.removeEventListener("scroll", handlePositionUpdate, true);
      window.removeEventListener("resize", handlePositionUpdate);
    };
  }, [step, demonstrationStore.refCard.current]);

  if (!isOpen) return null;

  if (location.pathname !== "/matches") {
    demonstrationStore.resetDemonstration();
    return null;
  }

  const handleClick = async () => {
    if (step === 3) {
      demonstrationStore.nextStep();
      return;
    }
    if (step === 4) {
      const user = getUserData();
      shopStore.markShopFingerDemoFinished();
      // hotfix: null-safe — раньше Game.game.id / user.id могли кинуть TypeError
      // (если контекст игры/юзера потерян) → клик по шагу 4 «падал», оверлей не
      // закрывался и юзер оставался заперт. closeDemonstration в любом случае
      // ставит isOpen=false (оверлей уходит), даже если gameId undefined.
      await demonstrationStore.closeDemonstration({
        gameId: Game.game?.id,
        userId: user?.id,
      });
      // item 11: на первом заходе в результаты НЕ открываем магазин авто-поп-апом.
      // По ТЗ клиента остаётся только поп-ап +1000 баллов; магазин доступен с
      // on-page CTA «Смотреть» и иконки магазина в зонах.
      return;
    }
    demonstrationStore.nextStep();
  };

  return (
    <>
      <div className="demonstration__background" />
      <div className="demonstration" onClick={handleClick}>
        <div className="demonstration__content">
          <div
            key={`help-${step}`}
            ref={helpRef}
            className={`demonstration__content--help demonstration__content--help__step-${step}`}
            style={{
              left: "var(--help-left-px)",
              top: "var(--help-top-px)",
            }}
          >
            {t(`demonstration.content.step-${step}`)}
          </div>
          <div
            key={`hand-${step}`}
            ref={handRef}
            className={`demonstration__content--hand demonstration__content--hand__step-${step}`}
            style={{
              left: "var(--hand-left-px)",
              top: "var(--hand-top-px)",
            }}
          >
            <img src={HandImage} alt="hand" />
          </div>
        </div>
      </div>
    </>
  );
});
